/**
 * "Which product is this campaign about?" — one answer, shared by every
 * surface that shows a campaign's product to a human.
 *
 * A single-product campaign has always answered this with the scalar
 * cr_campaigns.product_name. A MULTI-PRODUCT campaign (2026-09-11) still
 * fills those scalar columns, but only as a mirror of product #1 — see the
 * header of 20260910000002_cr_campaign_products.sql. Reading them directly on
 * a 4-product campaign therefore doesn't fail, it does something worse: it
 * confidently names one product and hides the other three.
 *
 * The right answer on a multi-product campaign is the BUNDLE: the single
 * TikTok Shop listing that contains every product on the campaign, which is
 * what a creator actually requests and receives. It is required (name,
 * description and photos all validated) on any live multi-product campaign,
 * so it is always there to show.
 *
 * Every caller goes through here so the wording can't drift between the
 * invite modal, the campaign filters, the chat job card and the emails. The
 * worker has its own copy of this rule for the emails it sends —
 * campaignProductName() in worker/src/campaignProduct.ts. Keep the two in
 * sync.
 */

/** The fields this needs. Loose on purpose: callers hand it whatever campaign
 *  shape they already hold (the shared list read, a page's own query, a
 *  worker payload), and anything missing just falls back. */
export interface CampaignProductSource {
  is_multi_product?: boolean | null
  bundle_product_name?: string | null
  product_name?: string | null
}

export interface CampaignProductIdentity {
  /** What to call the product — the bundle listing when there is one. */
  name: string | null
  /** True when `name` is the bundle rather than a single product. Lets a
   *  caller add its own marker where that distinction matters (a dropdown)
   *  and stay silent where it doesn't (a sentence in an email). */
  isBundle: boolean
}

/**
 * The product identity to show for a campaign.
 *
 * Falls back to the scalar product_name whenever the bundle isn't usable —
 * a draft multi-product campaign mid-setup, or a row saved before the bundle
 * columns existed. That fallback is the old behaviour exactly, so nothing
 * that worked before this existed can start showing blank.
 */
export function campaignProductIdentity(
  c: CampaignProductSource | null | undefined,
): CampaignProductIdentity {
  const bundle = c?.is_multi_product ? (c.bundle_product_name?.trim() || '') : ''
  if (bundle) return { name: bundle, isBundle: true }
  return { name: c?.product_name?.trim() || null, isBundle: false }
}

/** Just the name — for prose, where "(bundle)" would read as noise. */
export function campaignProductName(c: CampaignProductSource | null | undefined): string | null {
  return campaignProductIdentity(c).name
}

/**
 * "<campaign> — <product>", the label every campaign picker on the brand side
 * uses. Marks the bundle explicitly: in a plain <option> there is no room for
 * a badge, and a bundle name sitting where a product name used to sit would
 * otherwise be indistinguishable from one.
 */
export function campaignPickerLabel(
  c: (CampaignProductSource & { name?: string | null }) | null | undefined,
): string {
  const campaignName = c?.name?.trim() || 'Campaign'
  const { name, isBundle } = campaignProductIdentity(c)
  if (!name) return campaignName
  return `${campaignName} — ${name}${isBundle ? ' (bundle)' : ''}`
}
